import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { useEffect, useState } from 'react';
import { homeService } from './clients/ApiClients';
import { HomeReturn, HomeUpdate } from './clients/rust-api-client';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import {
  ArrowTopRightOnSquareIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/16/solid';
import {
  HOME_STATUSES,
  homeAddressLine,
  homeLocality,
  normalizeHomeStatus,
  propertyTypeLabel,
} from './home/homeMeta';
import ViolationList from './ViolationList';
import WorkOrderList from './WorkOrderList';
import { CreateActions } from './shell/CreateActions';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import EditHomeDialog from './home/EditHomeDialog';
import MemberListComponent from './member/MemberListComponent';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canViewLedger, canViewReservations } from './constants/UserTypes';
import { fresher, useSyncedHome } from './sync/hooks';
import { HomeSuspensionBadge } from './reservation/HomeSuspensionBadge';
import { apiErrorMessage } from './utils/apiErrors';
import { ErrorPage } from './common/Custom/ErrorPage';

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

const HomeDetailPage: React.FC = () => {
  const { hoaId, homeId } = useParams<{ hoaId: string; homeId: string }>();
  // The home's ledger, statement, coupon, tenures and estoppel are their
  // own routes — see docs/DUES_FRONTEND_PLAN.md. BoardMembers can read.
  const canSeeBilling = canViewLedger(getUserTypeFromToken());
  const canSeeReservations = canViewReservations(getUserTypeFromToken());
  const [loading, setLoading] = useState(true);
  const [fetchedHome, setHome] = useState<HomeReturn | null>(null);
  // The sync engine's local copy when it has this home (#561); fetched
  // otherwise.
  const syncedHome = useSyncedHome(homeId);
  const home = fresher(syncedHome, fetchedHome);
  const haveSynced = !!syncedHome;
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  // Why the home could not be loaded, and a counter "Try again" bumps to
  // run the load again.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  useEffect(() => {
    const fetchHoa = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setLoadError(null);
        const response = await homeService.getHome({
          id: homeId!,
        });
        setHome(response.data);
      } catch (err) {
        console.error('Error fetching home:', err);
        setLoadError(apiErrorMessage(err, "Couldn't load the home."));
      } finally {
        setLoading(false);
      }
    };

    fetchHoa();
  }, [hoaId, homeId, haveSynced, reloadKey]);

  const handleUpdateHome = async (updateData: HomeUpdate) => {
    try {
      setLoading(true);
      await homeService.updateHome({
        id: homeId!,
        homeUpdate: updateData,
      });
      // Refresh home data
      const response = await homeService.getHome({
        id: homeId!,
      });
      setHome(response.data);
      showAlert('success', 'Success', 'Home updated successfully!');
    } catch (err) {
      console.error('Error updating home:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't save the home.");
      showAlert('error', "Couldn't update home", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner />;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!home) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage="hoa_detail" />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this home"
          message={
            loadError ??
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  const status = home ? normalizeHomeStatus(home.status) : null;
  const statusMeta = HOME_STATUSES.find((st) => st.value === status);
  const fact = 'flex w-full items-baseline justify-between gap-3 px-2 py-1 text-[13px]';
  const sectionTitle = 'mb-1.5 px-2 text-[12px] font-medium text-fg-muted';
  const panelLink =
    'flex items-center justify-between rounded-md px-2 py-1 text-[13px] text-fg-secondary hover:bg-fg/5';

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="hoa_detail" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      {/* Header strip (#603 follow-on 3): back to this association's homes. */}
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}?tab=homes`} className="text-fg-muted hover:text-fg">
          Homes
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">
          {home ? homeAddressLine(home) : 'Unable to load address'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {/* Creates for this home, the home and its association fixed. */}
          {home && (
            <CreateActions
              scope={{ hoaId, homeId }}
              violation
              workOrder
              reservation={canSeeReservations}
            />
          )}
          <Dropdown>
            <DropdownButton plain aria-label="More actions" disabled={!home}>
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem onClick={() => setIsEditing(true)}>Edit details…</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      <ShowAlert
        alertType={alert.type}
        alertTitle={alert.title}
        alertMessage={alert.message}
        isVisible={alert.show}
        onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        timeout={5000}
        isInline={true}
      />

      {home && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-10">
            <div>
              <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
                {homeAddressLine(home)}
              </h1>
              <p className="mt-1 text-[15px]/6 text-fg-muted">{homeLocality(home)}</p>
              {canSeeReservations && hoaId && homeId && (
                <div className="mt-2">
                  <HomeSuspensionBadge hoaId={hoaId} homeId={homeId} />
                </div>
              )}
            </div>
            <MemberListComponent hoaId={hoaId} home={home} fetchAll={false} />
            <ViolationList hoaId={hoaId ?? ''} homeId={homeId} title="Violations" />
            <WorkOrderList hoaId={hoaId} homeId={homeId} title="Work orders" />
          </div>

          {/* On narrow screens the panel drops below the main column. */}
          <aside aria-label="Home properties" className="divide-y divide-line/60 lg:sticky lg:top-6 lg:self-start">
            <section className="pb-3">
              <h2 className={sectionTitle}>Properties</h2>
              <div className={fact}>
                <span className="text-fg-muted">Status</span>
                <span className="inline-flex items-center gap-1.5 text-fg">
                  <span className={`h-2 w-2 rounded-full ${statusMeta?.dot ?? 'bg-fg-faint'}`} aria-hidden />
                  {statusMeta?.label ?? home.status}
                </span>
              </div>
              <div className={fact}>
                <span className="text-fg-muted">Type</span>
                <span className="text-fg">{propertyTypeLabel(home.property_type)}</span>
              </div>
            </section>
            <section className="py-3">
              <h2 className={sectionTitle}>Location</h2>
              <div className={fact}>
                <span className="text-fg-muted">Street</span>
                <span className="truncate text-right text-fg">{homeAddressLine(home)}</span>
              </div>
              <div className={fact}>
                <span className="text-fg-muted">City</span>
                <span className="text-fg">{home.city}</span>
              </div>
              <div className={fact}>
                <span className="text-fg-muted">State / ZIP</span>
                <span className="text-fg">{[home.state, home.zip].filter(Boolean).join(' ')}</span>
              </div>
            </section>
            <section className="pt-3">
              <h2 className={sectionTitle}>Go to</h2>
              {/* The home's ledger, statement, coupon, tenures and estoppel
                  are their own routes — see docs/DUES_FRONTEND_PLAN.md. */}
              {canSeeBilling && (
                <Link to={`/hoa/${hoaId}/home/${homeId}/billing`} className={panelLink}>
                  Billing
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 text-fg-faint" aria-hidden />
                </Link>
              )}
              <Link to={`/hoa/${hoaId}/home/${homeId}/work-orders`} className={panelLink}>
                Work orders
                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-fg-faint" aria-hidden />
              </Link>
              {canSeeReservations && (
                <Link to={`/hoa/${hoaId}/home/${homeId}/reservations`} className={panelLink}>
                  Reservations
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 text-fg-faint" aria-hidden />
                </Link>
              )}
            </section>
          </aside>
        </div>
      )}

      {home && (
        <EditHomeDialog
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          onSave={handleUpdateHome}
          home={home}
        />
      )}
    </SidebarLayout>
  );
};

export default HomeDetailPage;
